import { InputValueType, PromptInput } from "#/promptInput"
import { useNovelCoreStore, useCodexEntryGroups, useNovelActsTree } from "@/stores/novelCoreStore"
import { cn, tiptapJsonToText } from "@/utils"
import { StopOutlined } from "@ant-design/icons"
import { Button, ButtonProps, Dropdown, DropdownProps, MenuItemProps, MenuProps, Select } from "antd"
import { BanIcon, CheckIcon, PlusIcon, TrashIcon, XIcon } from "lucide-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { codexBasicGroups } from "@/constants"
import { CoreEntityType } from "#/enum"
import Color from "color"
import { NovelActTreeType, NovelChapterSchema, NovelSceneSchema } from "#/novelLibrary"
import CodexEntryIcon from "@/components/Codex/CodexEntryIcon"

type ContentChooserInputProps = {
  value: PromptInput['default']
  onChange: (value: PromptInput['default']) => void
  inputOption: PromptInput
  className?: string
  triggerProps?: ButtonProps
}

type ValueViewItem = {
  type: InputValueType
  id: string
  icon?: React.ReactNode
  color?: string
  label: string
  description?: string
}

const inputValueType: Record<InputValueType, InputValueType> = {
  dropdown: 'dropdown',
  text: 'text',
  checkbox: 'checkbox',
  acts: 'acts',
  fullNovel: 'fullNovel',
  chapters: 'chapters',
  scenes: 'scenes',
  codexEntries: 'codexEntries',
  codexType: 'codexType',
  codexCategory: 'codexCategory',
  codexFields: 'codexFields',
  codexTag: 'codexTag',
  labels: 'labels',
  snippets: 'snippets',
}

const ContentChooserInput = ({ value, onChange, inputOption, className, triggerProps }: ContentChooserInputProps) => {
  const { snippets, codexCategories, codexFields, codexEntries, getCoreDataByType } = useNovelCoreStore()
  const codexEntryGroups = useCodexEntryGroups()
  const novelActTree = useNovelActsTree()

  const novelChapters = useMemo(() => {
    return novelActTree.flatMap(act => act.children || []) as (NovelActTreeType<NovelChapterSchema> & {
      children?: NovelActTreeType<NovelSceneSchema>[]
    })[]
  }, [novelActTree])

  const codexTags = useMemo(() => {
    //去重
    return [...new Set(codexEntries.flatMap(entry => entry.attributes.tags || []))]
  }, [codexEntries])

  const setting = inputOption.settings || {}
  const values = value?.length ? value : inputOption.default || []
  const selfValue = useMemo(() => {
    const result: Record<InputValueType, string[]> = {
      dropdown: [],
      text: [],
      checkbox: [],
      acts: [],
      fullNovel: [],
      chapters: [],
      scenes: [],
      codexEntries: [],
      codexType: [],
      codexCategory: [],
      codexFields: [],
      codexTag: [],
      labels: [],
      snippets: [],
    }

    values.forEach(item => {
      result[item.type].push(item.value)
    })
    return result
  }, [values])

  const [open, setOpen] = useState(false)

  const handleChange = (value: string | string[] | undefined) => {
    if (typeof value === 'string') {
      onChange([{ type: 'dropdown', value }])
    } else if (Array.isArray(value)) {
      onChange(value.map(item => ({ type: 'dropdown', value: item })))
    } else {
      onChange(value)
    }
  }

  const handleItemClick = useCallback((type: InputValueType, value: string) => {
    if (value === 'clear') {
      onChange(values.filter(item => item.type !== type))
      return
    }

    if (value === 'allType') {
      onChange(codexBasicGroups.map(item => ({ type, value: item.type })))
      return
    }

    if (inputOption.mode === 'single') {
      if (selfValue[type].includes(value)) {
        onChange([])
      } else {
        onChange([{ type, value }])
      }
    } else {
      if (selfValue[type].includes(value)) {
        onChange(values.filter(item => !(item.type === type && item.value === value)))
      } else {
        onChange(values.concat({ type, value }))
      }
    }
  }, [values, inputOption.mode, selfValue])

  const menuItems = useMemo<MenuProps['items']>(() => {
    const result: MenuProps['items'] = [
      {
        icon: <span className="size-4">{selfValue.fullNovel.includes('fullContent') ? <CheckIcon size={16} /> : <span></span>}</span>,
        label: <span className="flex flex-col gap-1">
          <span>整篇正文</span>
          <span className="text-secondary text-sm">注入全部正文，Token 消耗较高。</span>
        </span>,
        key: 'fullNovel',
        onClick: () => handleItemClick('fullNovel', 'fullContent')
      },
      {
        icon: <span className="size-4">{selfValue.fullNovel.includes('fullSummary') ? <CheckIcon size={16} /> : <span></span>}</span>,
        label: <span className="flex flex-col gap-1">
          <span>全文目录</span>
          <span className="text-secondary text-sm">目录大纲，含篇、章、节。</span>
        </span>,
        key: 'fullNovelOutline',
        onClick: () => handleItemClick('fullNovel', 'fullSummary')
      },
      {
        type: 'divider',
      },
    ]
    if (novelActTree.length) {
      result.push({
        icon: <span className="size-4">
          <span className={selfValue.acts.length ? "inline-block size-2 bg-tertiary rounded-full" : 'hidden'}></span>
        </span>,
        label: '篇',
        key: inputValueType.acts,
        popupClassName: 'min-w-[160px]',
        children: novelActTree.map(act => (
          {
            icon: <span className="size-4"><CheckIcon className={selfValue.acts.includes(act.id) ? "" : "hidden"} size={18} /></span>,
            label: <span className="flex flex-col gap-1">
              <span>{act.shortcutName}</span>
              <span className="text-secondary text-sm">共 {act.children?.length || 0} 个章节</span>
            </span>,
            key: act.id,
            onClick: () => handleItemClick('acts', act.id)
          }
        ))
      })
    }
    if (novelChapters.length) {
      result.push({
        label: '章节',
        icon: <span className="size-4">
          <span className={selfValue.chapters.length ? "inline-block size-2 bg-tertiary rounded-full" : 'hidden'}></span>
        </span>,
        key: inputValueType.chapters,
        popupClassName: 'min-w-[160px]',
        children: novelActTree.filter(act => act.children?.length).flatMap(act =>
          [
            {
              type: 'group',
              label: act.shortcutName
            },
            ...(act.children || []).map(chapter => (
              {
                icon: <span className="size-4"><CheckIcon className={selfValue.chapters.includes(chapter.id) ? "" : "hidden"} size={16} /></span>,
                label: <span className="flex flex-col gap-1">
                  <span>{chapter.shortcutName}</span>
                  <span className="text-secondary text-sm">共 {chapter.children?.length || 0} 个节</span>
                </span>,
                key: chapter.id,
                onClick: () => handleItemClick('chapters', chapter.id)
              }
            ))
          ])
      })
    }
    if (novelChapters.some(chapter => chapter.children?.length)) {
      result.push({
        label: '节',
        icon: <span className="size-4">
          <span className={selfValue.scenes.length ? "inline-block size-2 bg-tertiary rounded-full" : 'hidden'}></span>
        </span>,
        key: inputValueType.scenes,
        popupClassName: 'min-w-[160px]',
        children: novelChapters.filter(chapter => chapter.children?.length).flatMap(chapter =>
          [
            {
              type: 'group',
              label: chapter.shortcutName
            },
            ...(chapter.children || []).map((scene: NovelActTreeType<NovelSceneSchema>) => (
              {
                icon: <span className="size-4"><CheckIcon className={selfValue.scenes.includes(scene.id) ? "" : "hidden"} size={18} /></span>,
                label: <span className="flex flex-col gap-1">
                  <span>{scene.shortcutName}</span>
                  {!!scene.data.attributes.summary && <span className="text-secondary text-sm line-clamp-3 text-ellipsis">{scene.data.attributes.summary}</span>}
                </span>,
                key: scene.id,
                onClick: () => handleItemClick('scenes', scene.id)
              }
            ))
          ])
      })
    }
    if (snippets.length) {
      result.push({
        label: '片段',
        icon: <span className="size-4">
          <span className={selfValue.snippets.length ? "inline-block size-2 bg-tertiary rounded-full" : 'hidden'}></span>
        </span>,
        key: inputValueType.snippets,
        popupClassName: 'min-w-[160px]',
        children: snippets.map(snippet => (
          {
            icon: <span className="size-4"><CheckIcon className={selfValue.snippets.includes(snippet.id) ? "" : "hidden"} size={18} /></span>,
            label: <span className="flex flex-col gap-1">
              <span>{snippet.attributes.title}</span>
              <span className="text-secondary text-sm line-clamp-3 text-ellipsis">{tiptapJsonToText(snippet.attributes.content)}</span>
            </span>,
            key: snippet.id,
            onClick: () => handleItemClick('snippets', snippet.id)
          }
        ))
      })
    }
    if (result[result.length - 1]?.type !== 'divider') {
      result.push({
        type: 'divider',
      })
    }
    result.push({
      label: '资料条目',
      icon: <span className="size-4">
        <span className={selfValue.codexEntries.length ? "inline-block size-2 bg-tertiary rounded-full" : 'hidden'}></span>
      </span>,
      key: inputValueType.codexEntries,
      disabled: codexEntryGroups.length === 0,
      popupClassName: 'min-w-[160px]',
      children: codexEntryGroups.flatMap(group => (
        [
          {
            type: 'group',
            label: group.label
          },
          ...group.children.map(entry => (
            {
              icon: <span className="size-4"><CheckIcon className={selfValue.codexEntries.includes(entry.id) ? "" : "hidden"} size={18} /></span>,
              label: <span className="flex flex-col gap-1">
                <span className="truncate">{entry.attributes.name}</span>
              </span>,
              key: entry.id,
              onClick: () => handleItemClick('codexEntries', entry.id)
            }
          ))
        ]
      ))
    })

    result.push({
      label: '资料类型',
      icon: <span className="size-4">
        <span className={selfValue.codexType.length ? "inline-block size-2 bg-tertiary rounded-full" : 'hidden'}></span>
      </span>,
      key: inputValueType.codexType,
      popupClassName: 'min-w-[160px]',
      children: [
        {
          type: 'group',
          label: '基础类型',
        },
        ...codexBasicGroups.map(group => (
          {
            icon: <span className="size-4"><CheckIcon className={selfValue.codexType.includes(group.type) ? "" : "hidden"} size={18} /></span>,
            label: group.label,
            key: group.type,
            onClick: () => handleItemClick('codexType', group.type)
          }
        )),
        {
          type: 'divider',
        },
        {
          icon: <span className="size-4"><CheckIcon className="hidden" size={16} /></span>,
          label: '所有类型',
          key: 'allType',
          onClick: () => handleItemClick('codexType', 'allType')
        },
        {
          icon: <span className="size-4"><BanIcon size={16} /></span>,
          label: '清除选中',
          key: 'clearCodexType',
          onClick: () => handleItemClick('codexType', 'clear')
        }
      ]
    })

    result.push({
      label: '资料分组',
      icon: <span className="size-4">
        <span className={selfValue.codexCategory.length ? "inline-block size-2 bg-tertiary rounded-full" : 'hidden'}></span>
      </span>,
      key: inputValueType.codexCategory,
      disabled: codexCategories.length === 0,
      popupClassName: 'min-w-[160px]',
      children: codexCategories.map(category => (
        {
          icon: <span className="size-4"><CheckIcon className={selfValue.codexCategory.includes(category.id) ? "" : "hidden"} size={18} /></span>,
          label: category.attributes.name,
          key: category.id,
          onClick: () => handleItemClick('codexCategory', category.id)
        }
      ))
    })
    result.push({
      label: '资料字段',
      icon: <span className="size-4">
        <span className={selfValue.codexFields.length ? "inline-block size-2 bg-tertiary rounded-full" : 'hidden'}></span>
      </span>,
      key: inputValueType.codexFields,
      disabled: codexFields.length === 0,
      popupClassName: 'min-w-[160px]',
      children: codexFields.map(field => (
        {
          icon: <span className="size-4"><CheckIcon className={selfValue.codexFields.includes(field.id) ? "" : "hidden"} size={18} /></span>,
          label: field.attributes.name,
          key: field.id,
          onClick: () => handleItemClick('codexFields', field.id)
        }
      ))
    })
    result.push({
      label: '资料标签',
      icon: <span className="size-4">
        <span className={selfValue.codexTag.length ? "inline-block size-2 bg-tertiary rounded-full" : 'hidden'}></span>
      </span>,
      key: inputValueType.codexTag,
      disabled: codexTags.length === 0,
      popupClassName: 'min-w-[160px]',
      children: codexTags.map((tag, index) => (
        {
          icon: <span className="size-4"><CheckIcon className={selfValue.codexTag.includes(tag) ? "" : "hidden"} size={18} /></span>,
          label: tag,
          key: index,
          onClick: () => handleItemClick('codexTag', tag)
        }
      ))
    })

    result.push({
      type: 'divider',
    })
    result.push({
      icon: <span ><BanIcon size={16} /></span>,
      label: '清除选中',
      key: 'clear'
    })
    return result
  }, [selfValue, novelActTree, novelChapters, snippets, inputOption.mode])

  const valueViewItems = useMemo<ValueViewItem[]>(() => {
    const result: ValueViewItem[] = []
    if (selfValue.fullNovel.includes('fullContent')) {
      result.push({
        type: 'fullNovel',
        id: 'fullContent',
        icon: <span><CheckIcon size={16} /></span>,
        label: '整篇正文',
        description: '共 124 个字',
      })
    }
    if (selfValue.fullNovel.includes('fullSummary')) {
      result.push({
        type: 'fullNovel',
        id: 'fullSummary',
        icon: <span><CheckIcon size={16} /></span>,
        label: '全文目录',
        description: '共 3 个节',
      })
    }

    if (selfValue.acts.length) {
      const acts = novelActTree.filter(act => selfValue.acts.includes(act.id))
      acts.forEach(act => {
        result.push({
          type: 'acts',
          id: act.id,
          label: act.shortcutName,
          description: `共 ${act.children?.length || 0} 个章节`,
        })
      })
    }

    if (selfValue.chapters.length) {
      const chapters = novelChapters.filter(chapter => selfValue.chapters.includes(chapter.id))
      chapters.forEach(chapter => {
        result.push({
          type: 'chapters',
          id: chapter.id,
          label: chapter.shortcutName,
          description: `共 ${chapter.children?.length || 0} 个节`,
        })
      })
    }
    if (selfValue.scenes.length) {
      novelChapters.forEach(chapter => {
        chapter.children?.forEach(scene => {
          if (selfValue.scenes.includes(scene.id)) {
            result.push({
              type: 'scenes',
              id: scene.id,
              label: chapter.shortcutName,
              description: scene.shortcutName,
            })
          }
        })
      })

    }
    if (selfValue.snippets.length) {
      snippets.forEach(snippet => {
        if (selfValue.snippets.includes(snippet.id)) {
          result.push({
            type: 'snippets',
            id: snippet.id,
            label: snippet.attributes.title,
            description: tiptapJsonToText(snippet.attributes.content),
          })
        }
      })
    }

    if (selfValue.codexEntries.length) {
      codexEntries.forEach(entry => {
        console.log('entry', entry)
        if (selfValue.codexEntries.includes(entry.id)) {
          result.push({
            type: 'codexEntries',
            color: entry.attributes.color,
            id: entry.id,
            label: entry.attributes.name || '',
            icon: <CodexEntryIcon entry={entry} />,
          })
        }
      })
    }
    if (selfValue.codexType.length) {
      codexBasicGroups.forEach(group => {
        console.log('group', group)
        if (selfValue.codexType.includes(group.type)) {
          result.push({
            type: 'codexType',
            id: group.type,
            label: group.label,
            description: `${codexEntries.filter(entry => entry.attributes.type === group.type).length} 个条目`,
            icon: <CodexEntryIcon type={group.type} />,
          })
        }
      })
    }
    if (selfValue.codexCategory.length) {
      codexCategories.forEach(category => {
        if (selfValue.codexCategory.includes(category.id)) {
          result.push({
            type: 'codexCategory',
            color: category.attributes.color,
            id: category.id,
            label: category.attributes.name,
            description: `${codexEntryGroups.find(group => group.category?.id === category.id)?.children.length || 0} 个条目`,
          })
        }
      })
    }

    if (selfValue.codexFields.length) {
      codexFields.forEach(field => {
        if (selfValue.codexFields.includes(field.id)) {
          result.push({
            type: 'codexFields',
            id: field.id,
            label: field.attributes.name,
            description: `${codexEntries.filter(entry => entry.fields?.some(f => f.id === field.id)).length || 0} 个条目`,
          })
        }
      })
    }

    if (selfValue.codexTag.length) {
      codexTags.forEach(tag => {
        if (selfValue.codexTag.includes(tag)) {
          result.push({
            type: 'codexTag',
            id: tag,
            label: tag,
            description: `${codexEntries.filter(entry => entry.attributes.tags?.includes(tag)).length || 0} 个条目`,
          })
        }
      })
    }
    return result
  }, [selfValue, snippets, novelChapters, novelActTree])

  return <div className={cn("flex gap-1 flex-wrap", className)}>
    <Dropdown
      trigger={['click']}
      open={open}
      onOpenChange={setOpen}
      menu={{
        items: menuItems,
      }}>
      <Button className="h-[38px] px-1.5" icon={<span><PlusIcon size={18} /></span>} {...triggerProps}>{inputOption.name}</Button>
    </Dropdown>
    {valueViewItems.map(item => (
      <div
        key={item.id}
        className={cn("h-[38px] flex items-center gap-2 rounded py-1 px-2 transition-colors max-w-[46cqw]", item.color
          ? `bg-[${Color(item.color).alpha(0.4).toString()}] hover:bg-[${Color(item.color).alpha(0.6).toString()}]`
          : 'bg-fill-3 hover:bg-fill-2')} >
        {item.icon && <div style={{ backgroundColor: item.color }}>{item.icon}</div>}
        <div className="flex flex-col overflow-hidden">
          <span className="truncate text-xs/tight">{item.label}</span>
          <span className="truncate text-secondary text-xs/tight">{item.description}</span>
        </div>
        <Button
          size="small"
          type="text"
          icon={<span><XIcon size={14} /></span>}
          onClick={() => handleItemClick(item.type, item.id)}
        />
      </div>
    ))}
  </div>
}

export default ContentChooserInput